/**
 * AI Workforce Dashboard — Round 14 (Sep 12 2026)
 * Eleven agents on their own cadences, one escalation queue for humans,
 * living insight cards per agent, and consoles for Topaz / Onyx /
 * Obsidian / Opal / Peridot. The overview polls once a minute with an
 * in-flight guard (the old 20s Promise.allSettled storm + unindexed
 * /trends is what froze the panel).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAdmin, canAccessAgent } from '../AdminApp';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import OpalPage from './OpalPage';
import TopazDashboard from './TopazDashboard';
import OnyxDashboard from './OnyxDashboard';
import ObsidianDashboard from './ObsidianDashboard';
import LapisPage from './LapisPage';
import TanzanitePage from './TanzanitePage';
import TourmalinePage from './TourmalinePage';
import SpinelPage from './SpinelPage';
import AmethystPage from './AmethystPage';   // Round 64: Behavioral Analytics & Social Coach
import {
  RefreshCw, Activity, Shield, AlertTriangle, DollarSign,
  HeadphonesIcon, TrendingUp, Heart, Gem, Play, Pause,
  Eye, AlertCircle, RotateCw, ChevronRight, BarChart3, Zap, Radio,
  Power, PowerOff, Megaphone, Bell, CheckCircle, Lightbulb, Send, X, Users, Newspaper, Lock, ShieldCheck, CalendarClock, Share2, Trophy,
} from 'lucide-react';

const AGENTS = [
  { key: 'OPAL',     label: 'Opal',     icon: Eye,            color: '#14b8a6' },
  { key: 'QUARTZ',   label: 'Quartz',   icon: Activity,       color: '#06b6d4' },
  { key: 'ONYX',     label: 'Onyx',     icon: Shield,         color: '#374151' },
  { key: 'OBSIDIAN', label: 'Obsidian', icon: AlertTriangle,  color: '#581c87' },
  { key: 'TOPAZ',    label: 'Topaz',    icon: HeadphonesIcon, color: '#f59e0b' },
  { key: 'CITRINE',  label: 'Citrine',  icon: DollarSign,     color: '#eab308' },
  { key: 'GARNET',   label: 'Garnet',   icon: TrendingUp,     color: '#ef4444' },
  { key: 'AMETHYST', label: 'Amethyst', icon: BarChart3,      color: '#a855f7' },
  { key: 'JADE',     label: 'Jade',     icon: Heart,          color: '#10b981' },
  { key: 'PERIDOT',  label: 'Peridot',  icon: Megaphone,      color: '#84cc16' },
  { key: 'SAPPHIRE', label: 'Sapphire', icon: Newspaper,      color: '#2563eb' },   // Round 23: current events
  { key: 'ALEXANDRITE', label: 'Alexandrite', icon: CheckCircle, color: '#0f766e' },  // Round 36: Chief of Staff (Decisions + Daily Brief)
  { key: 'LAPIS',    label: 'Lapis',    icon: ShieldCheck,    color: '#1d4ed8' },  // Round 41: Compliance & Brand Guard
  { key: 'TANZANITE', label: 'Tanzanite', icon: CalendarClock, color: '#4f46e5' },  // Round 42: Live Programming
  { key: 'TOURMALINE', label: 'Tourmaline', icon: Share2, color: '#db2777' },  // Round 48: Social Media & Content
  { key: 'SPINEL', label: 'Spinel', icon: Trophy, color: '#d97706' },             // Round 53: Tournament Director
  { key: 'DIAMOND',  label: 'Diamond',  icon: Gem,            color: '#8b5cf6' },
];

const STATUS_STYLES = {
  active:     { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  idle:       { bg: 'bg-gray-100',    text: 'text-gray-600',    dot: 'bg-gray-400' },
  processing: { bg: 'bg-blue-100',    text: 'text-blue-700',    dot: 'bg-blue-500' },
  error:      { bg: 'bg-red-100',     text: 'text-red-700',     dot: 'bg-red-500' },
  disabled:   { bg: 'bg-gray-100',    text: 'text-gray-400',    dot: 'bg-gray-300' },
};

const SEVERITY_STYLES = {
  critical: 'bg-red-100 text-red-800',
  high:     'bg-orange-100 text-orange-800',
  medium:   'bg-amber-100 text-amber-800',
  low:      'bg-blue-100 text-blue-800',
  info:     'bg-gray-100 text-gray-600',
};

const SEV_COLORS = { critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#3b82f6' };

const fmtInterval = (s) => (!s ? '—' : s >= 3600 ? `${Math.round(s / 3600)}h` : s >= 60 ? `${Math.round(s / 60)}m` : `${s}s`);
const ago = (iso) => {
  if (!iso) return 'never';
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return `${Math.round(d)}s ago`;
  if (d < 3600) return `${Math.round(d / 60)}m ago`;
  if (d < 86400) return `${Math.round(d / 3600)}h ago`;
  return `${Math.round(d / 86400)}d ago`;
};


/* ─── SIDEBAR ─── */
const AgentNav = ({ activeAgent, escalationCount }) => {
  const navigate = useNavigate();
  const { admin } = useAdmin();
  return (
    <div className="w-56 shrink-0 border-r bg-gray-50/60 overflow-y-auto" data-testid="workforce-sidebar">
      <div className="px-3 py-3">
        <button onClick={() => navigate('/workforce')}
          className={`w-full text-left px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition ${
            !activeAgent ? 'bg-violet-100 text-violet-800' : 'text-gray-600 hover:bg-gray-100'}`}
          data-testid="workforce-overview-btn">
          <Gem className="w-4 h-4" /> Overview
          {escalationCount > 0 && <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-bold">{escalationCount}</span>}
        </button>
      </div>
      <div className="px-3 pb-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Agents</div>
      <div className="px-2 pb-4 space-y-0.5">
        {AGENTS.map(a => {
          // Round 39: agents this admin was not given are greyed out (and refused by the API)
          const allowed = canAccessAgent(admin, a.key);
          return (
          <button key={a.key}
            onClick={() => allowed && navigate(`/workforce/${a.key.toLowerCase()}`)}
            disabled={!allowed}
            title={allowed ? undefined : "You don't have access to this agent"}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition ${
              !allowed ? 'text-gray-300 cursor-not-allowed'
              : activeAgent === a.key ? 'bg-white shadow-sm font-semibold text-gray-900' : 'text-gray-600 hover:bg-gray-100'}`}
            data-testid={`nav-agent-${a.key.toLowerCase()}`}>
            <div className={`w-6 h-6 rounded flex items-center justify-center ${allowed ? '' : 'opacity-30'}`} style={{ background: a.color }}>
              <a.icon className="w-3.5 h-3.5 text-white" />
            </div>
            {a.label}
            {!allowed && <Lock className="w-3 h-3 ml-auto" />}
          </button>
          );
        })}
      </div>
    </div>
  );
};


/* ─── STATUS BADGE ─── */
const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.idle;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {status?.toUpperCase()}
    </span>
  );
};

/* ─── HEARTBEAT PULSE ─── */
const HeartbeatDot = ({ alive, secondsSince }) => {
  if (!alive) return <span className="w-2 h-2 rounded-full bg-gray-300" title="No heartbeat" />;
  const fresh = secondsSince == null || secondsSince < 60;
  return (
    <span className="relative flex h-2.5 w-2.5" title={secondsSince != null ? `Last heartbeat ${Math.round(secondsSince)}s ago` : 'Alive'}>
      {fresh && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />}
      <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${fresh ? 'bg-emerald-500' : 'bg-amber-400'}`} />
    </span>
  );
};

/* ─── MINI BAR CHART (severity breakdown) ─── */
const SeverityBar = ({ data }) => {
  const total = Object.values(data || {}).reduce((s, v) => s + v, 0);
  if (!total) return <span className="text-xs text-gray-400">No events in 24h</span>;
  return (
    <div className="flex gap-0.5 h-5 rounded overflow-hidden w-full" data-testid="severity-bar">
      {['critical', 'high', 'medium', 'low'].map(sev => {
        const pct = ((data[sev] || 0) / total) * 100;
        if (!pct) return null;
        return <div key={sev} style={{ width: `${pct}%`, background: SEV_COLORS[sev] }} title={`${sev}: ${data[sev]}`} className="min-w-[4px]" />;
      })}
    </div>
  );
};

/* ─── HEALTH HEATMAP ─── */
const HealthHeatmap = ({ agents }) => {
  const statusColor = (s) => {
    if (s === 'active') return '#10b981';
    if (s === 'processing') return '#3b82f6';
    if (s === 'error') return '#ef4444';
    return '#d1d5db';
  };
  return (
    <div className="grid grid-cols-6 gap-1.5" data-testid="health-heatmap">
      {agents.map(a => (
        <div key={a.name} className="rounded-lg p-2 text-center text-[10px] font-medium text-white"
          style={{ background: statusColor(a.status) }} title={`${a.name}: ${a.status}${a.last_error ? ` — ${a.last_error}` : ''}`}>
          {a.name.slice(0, 3)}
        </div>
      ))}
    </div>
  );
};

/* ─── INSIGHT CARD ─── */
const InsightCard = ({ ins }) => (
  <div className={`rounded-xl border p-4 ${ins.severity === 'critical' ? 'border-red-200 bg-red-50' : ins.severity === 'high' ? 'border-orange-200 bg-orange-50' : ins.severity === 'medium' ? 'border-amber-200 bg-amber-50' : 'bg-white'}`} data-testid={`insight-${ins.agent}-${ins.key}`}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{ins.agent} · {ins.key.replace(/_/g, ' ')}</span>
      <span className="text-[10px] text-gray-400">{ago(ins.updated_at)}</span>
    </div>
    <p className="text-sm font-semibold text-gray-900">{ins.title}</p>
    <p className="text-xs text-gray-600 mt-1 whitespace-pre-line">{ins.summary}</p>
  </div>
);

/* ─── ESCALATION ROW ─── */
const EscalationRow = ({ e, onResolve }) => {
  const navigate = useNavigate();
  // Round 26: the title opens the escalation panel on the agent's page
  const target = `/workforce/${(e.agent || '').toLowerCase()}?escalation=${e.id}`;
  return (
    <div className="px-4 py-3 flex items-start gap-3 text-sm" data-testid={`escalation-${e.id}`}>
      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase shrink-0 mt-0.5 ${SEVERITY_STYLES[e.severity] || ''}`}>{e.severity}</span>
      <div className="min-w-0 flex-1">
        <button onClick={() => navigate(target)} className="font-medium text-gray-900 hover:underline text-left">{e.title}</button>
        <span className="text-gray-400 ml-2 text-[10px]">{e.agent} · {ago(e.created_at)}{e.channels?.email ? ` · emailed ${e.channels.email}` : ''}{e.channels?.push ? ` · pushed ${e.channels.push}` : ''}</span>
        <p className="text-xs text-gray-500 whitespace-pre-line line-clamp-3">{e.message}</p>
      </div>
      <button onClick={() => onResolve(e.id)} className="text-xs px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 shrink-0 flex items-center gap-1" data-testid={`resolve-escalation-${e.id}`}>
        <CheckCircle className="w-3 h-3" /> Done
      </button>
    </div>
  );
};


/* ─── ESCALATION PANEL (Round 26) ─── */
// Opened by ?escalation=<id> — from the bell, a push, an email or the
// Escalations tab. Shows what went wrong, what the agent found and did,
// whether it still needs a person, and lets you resolve it with notes.
const relatedTarget = (e) => {
  if (!e) return null;
  if (e.related_type === 'ticket') return { path: '/support-tickets', label: 'Open the ticket' };
  if (e.related_type === 'user' && e.related_id) return { path: `/users?q=${encodeURIComponent(e.related_id)}`, label: 'Open the member' };
  if (e.related_type === 'stream') return { path: '/streams', label: 'Open streams' };
  if (e.related_type === 'violation') return { path: '/workforce/onyx', label: 'Open Onyx' };
  if (e.related_type === 'fraud_alert') return { path: '/fraud-alerts', label: 'Open fraud alerts' };
  if (e.related_type === 'qa_run') return { path: '/workforce/opal', label: 'Open Opal' };
  return null;
};

const EscalationPanel = ({ escalationId, onClose }) => {
  const { api } = useAdmin();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`/api/admin/workforce/escalations/${escalationId}`); setD(r.data); setMissing(false); }
    catch { setMissing(true); }
  }, [api, escalationId]);
  useEffect(() => { setD(null); setNotes(''); load(); }, [load]);

  const resolve = async () => {
    setBusy(true);
    try { await api.post(`/api/admin/workforce/escalations/${escalationId}/resolve`, { notes }); toast.success('Marked resolved'); await load(); }
    catch { toast.error('Could not resolve'); } finally { setBusy(false); }
  };
  const reopen = async () => {
    setBusy(true);
    try { await api.post(`/api/admin/workforce/escalations/${escalationId}/reopen`); toast.success('Reopened — it needs attention again'); await load(); }
    catch { toast.error('Could not reopen'); } finally { setBusy(false); }
  };

  if (missing) return (
    <div className="bg-white border rounded-xl p-4 text-sm text-gray-600 flex items-center justify-between" data-testid="escalation-panel-missing">
      <span>This escalation no longer exists.</span>
      <button onClick={onClose} className="text-xs px-2 py-1 border rounded-lg">Close</button>
    </div>
  );
  if (!d) return <div className="bg-white border rounded-xl p-4 text-sm text-gray-400" data-testid="escalation-panel-loading">Loading escalation…</div>;
  const e = d.escalation || {};
  const open = e.status === 'open';
  const rel = relatedTarget(e);
  const dataRows = Object.entries(e.data || {}).filter(([, v]) => v !== null && v !== undefined && v !== '' && typeof v !== 'object');
  const dataObjs = Object.entries(e.data || {}).filter(([, v]) => v && typeof v === 'object');
  return (
    <div className={`bg-white border-2 rounded-xl p-5 space-y-4 ${open ? 'border-red-200' : 'border-emerald-200'}`} data-testid="escalation-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${SEVERITY_STYLES[e.severity] || ''}`}>{e.severity}</span>
            <span className="text-xs text-gray-500">{e.agent} · {e.kind || 'escalation'} · {e.created_at ? new Date(e.created_at).toLocaleString() : ''}</span>
            {open
              ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-800 font-bold uppercase">Needs attention</span>
              : e.status === 'superseded'
                ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-bold uppercase" title="A newer escalation about the same thing replaced this one">Superseded</span>
              : e.status === 'expired'
                ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-bold uppercase" title="Nobody acted on it for 14 days, so it timed out">Timed out</span>
              : <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold uppercase">Resolved</span>}
          </div>
          <h2 className="text-lg font-bold text-gray-900 mt-1">{e.title}</h2>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0" title="Close" data-testid="escalation-close"><X className="w-5 h-5" /></button>
      </div>

      <div>
        <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">What's wrong</div>
        <p className="text-sm text-gray-800 whitespace-pre-line">{e.message}</p>
        {(e.related_type || e.related_id) && (
          <p className="text-xs text-gray-500 mt-1">About: {e.related_type} {e.related_id ? <code className="bg-gray-100 px-1 rounded">{e.related_id}</code> : ''}
            {d.related && <span className="ml-2 text-gray-700">— {d.related.subject || d.related.title || d.related.name || d.related.handle || ''}{d.related.status ? ` (${d.related.status})` : ''}</span>}
            {rel && <button onClick={() => navigate(rel.path)} className="ml-2 text-emerald-700 hover:underline font-medium">{rel.label} →</button>}
          </p>
        )}
      </div>

      {/* Round 39: every escalation says what a person should do about it */}
      {e.suggested_action && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3" data-testid="escalation-suggested-action">
          <div className="text-[11px] font-bold text-emerald-700 uppercase tracking-wide mb-1">Suggested action</div>
          <p className="text-sm text-emerald-900 whitespace-pre-line">{e.suggested_action}</p>
        </div>
      )}

      {(dataRows.length > 0 || dataObjs.length > 0) && (
        <div>
          <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">What the agent found</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 text-sm">
            {dataRows.map(([k, v]) => <div key={k} className="flex justify-between border-b border-gray-50 py-1"><span className="text-gray-500">{k.replace(/_/g, ' ')}</span><span className="text-gray-900 font-medium text-right ml-3 break-all">{String(v)}</span></div>)}
          </div>
          {dataObjs.length > 0 && <pre className="mt-2 text-[11px] bg-gray-50 rounded-lg p-3 overflow-x-auto max-h-48">{JSON.stringify(Object.fromEntries(dataObjs), null, 2)}</pre>}
        </div>
      )}

      <div>
        <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">What the agent did</div>
        {(d.actions || []).length === 0 ? (
          <p className="text-sm text-gray-500">No automatic action was taken — this is waiting on a person.</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {d.actions.map((a) => (
              <div key={a.action_id} className="py-1.5 text-sm flex items-start gap-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded mt-0.5 shrink-0 ${a.executed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{a.executed ? 'DONE' : 'PROPOSED'}</span>
                <div className="min-w-0"><span className="font-medium text-gray-900">{(a.action_type || '').replace(/_/g, ' ')}</span>{a.target_type ? <span className="text-gray-500"> on {a.target_type} {a.target_id}</span> : null}
                  {a.reason && <div className="text-xs text-gray-600">{a.reason}</div>}
                </div>
                <span className="ml-auto text-[10px] text-gray-400 shrink-0">{a.executed_at ? new Date(a.executed_at).toLocaleString() : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {(d.events || []).length > 0 && (
        <div>
          <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">Related events</div>
          <div className="divide-y divide-gray-50">
            {d.events.slice(0, 8).map((ev) => (
              <div key={ev.event_id} className="py-1 text-sm flex items-center gap-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${SEVERITY_STYLES[ev.severity] || ''}`}>{ev.severity}</span>
                <span className="text-gray-900">{ev.title}</span>{ev.resolved && <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">RESOLVED</span>}
                <span className="ml-auto text-[10px] text-gray-400">{ev.created_at ? new Date(ev.created_at).toLocaleTimeString() : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(d.logs || []).length > 0 && (
        <div>
          <button onClick={() => setShowLogs((s) => !s)} className="text-xs text-gray-500 hover:text-gray-700">{showLogs ? 'Hide' : 'Show'} the agent's log around this time ({d.logs.length})</button>
          {showLogs && (
            <div className="mt-2 bg-gray-900 rounded-lg p-3 font-mono text-[11px] max-h-48 overflow-y-auto">
              {d.logs.map((l, i) => <div key={i}><span className="text-gray-500">{l.timestamp ? new Date(l.timestamp).toLocaleTimeString() : ''}</span> <span className={l.level === 'error' ? 'text-red-400' : l.level === 'warning' ? 'text-amber-400' : 'text-emerald-400'}>[{l.level}]</span> <span className="text-gray-200">{l.message}</span></div>)}
            </div>
          )}
        </div>
      )}

      <div className="border-t pt-3">
        {open ? (
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <label className="flex-1 text-xs text-gray-600">What was done / why it's fine
              <textarea value={notes} onChange={(ev) => setNotes(ev.target.value)} rows={2} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" placeholder="e.g. Refunded the member and warned the sender" data-testid="escalation-notes" />
            </label>
            <button onClick={resolve} disabled={busy} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1" data-testid="escalation-resolve"><CheckCircle className="w-4 h-4" /> Mark resolved</button>
          </div>
        ) : (
          <div className="text-sm text-gray-700 flex flex-wrap items-center gap-2">
            <span><strong>{e.status === 'superseded' ? 'Superseded' : e.status === 'expired' ? 'Timed out' : 'Resolved'}</strong>{e.resolved_at ? ` ${new Date(e.resolved_at).toLocaleString()}` : ''}{e.resolved_by ? ` by ${e.resolved_by}` : ''}.</span>
            {(e.notes || e.resolution_note) ? <span className="text-gray-600">Notes: {e.notes || e.resolution_note}</span> : <span className="text-gray-400">No notes.</span>}
            <button onClick={reopen} disabled={busy} className="ml-auto text-xs px-2 py-1 border rounded-lg text-gray-600 hover:bg-gray-50">Reopen — needs attention</button>
          </div>
        )}
      </div>
    </div>
  );
};


/* ─── OVERVIEW PAGE ─── */
const OverviewPage = ({ onEscalationCount }) => {
  const { api } = useAdmin();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [agents, setAgents] = useState([]);
  const [trends, setTrends] = useState(null);
  const [heartbeats, setHeartbeats] = useState({});
  const [escalations, setEscalations] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [failed, setFailed] = useState([]);
  const inFlight = useRef(false);

  const fetchData = useCallback(async (manual = false) => {
    if (inFlight.current) return;
    inFlight.current = true;
    const calls = {
      dashboard: '/api/admin/workforce/dashboard',
      agents: '/api/admin/workforce/agents',
      trends: '/api/admin/workforce/trends',
      heartbeats: '/api/admin/workforce/heartbeats',
      escalations: '/api/admin/workforce/escalations?status=open&limit=20',
      insights: '/api/admin/workforce/insights',
    };
    const keys = Object.keys(calls);
    const results = await Promise.allSettled(keys.map(k => api.get(calls[k])));
    const errs = [];
    results.forEach((r, i) => {
      const k = keys[i];
      if (r.status !== 'fulfilled') {
        errs.push(k);
        console.error(`[Workforce] ${calls[k]} failed`, r.reason);
        return;
      }
      const d = r.value.data;
      if (k === 'dashboard') { setDashboard(d); setScannerActive(!!d.background_scanner_active); }
      else if (k === 'agents') setAgents(d.agents || []);
      else if (k === 'trends') setTrends(d);
      else if (k === 'heartbeats') { const m = {}; (d.heartbeats || []).forEach(h => { m[h.agent_name] = h; }); setHeartbeats(m); }
      else if (k === 'escalations') { setEscalations(d.escalations || []); onEscalationCount?.(d.open_count || 0); }
      else if (k === 'insights') setInsights(d.insights || []);
    });
    setFailed(errs);
    if (manual && errs.length) toast.error(`Failed to load: ${errs.join(', ')}`);
    inFlight.current = false;
    setLoading(false);
  }, [api, onEscalationCount]);

  useEffect(() => { fetchData(); const i = setInterval(() => fetchData(false), 60000); return () => clearInterval(i); }, [fetchData]);

  const runAll = async () => {
    setRunning(true);
    try { await api.post('/api/admin/workforce/run'); toast.success('All agents started — results land as each one finishes'); setTimeout(() => fetchData(false), 8000); }
    catch { toast.error('Run failed'); }
    finally { setRunning(false); }
  };

  const toggleScanner = async () => {
    try {
      if (scannerActive) { await api.post('/api/admin/workforce/scanner/stop'); setScannerActive(false); toast.success('Background loops stopped'); }
      else { await api.post('/api/admin/workforce/scanner/start'); setScannerActive(true); toast.success('Background loops started'); }
    } catch { toast.error('Failed to toggle scanner'); }
  };

  const resolveEscalation = async (id) => {
    try {
      await api.post(`/api/admin/workforce/escalations/${id}/resolve`, { notes: '' });
      setEscalations(prev => prev.filter(e => e.id !== id));
      onEscalationCount?.(Math.max(0, escalations.length - 1));
      toast.success('Escalation resolved');
    } catch { toast.error('Could not resolve'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 animate-spin text-violet-500" /></div>;
  const summary = dashboard?.summary || {};
  const events = dashboard?.recent_events || [];
  const sevBreakdown = trends?.severity_breakdown || {};
  const hq = summary.human_queue || {};
  const humanTotal = (hq.tickets_waiting || 0) + (hq.violations_waiting || 0) + (hq.fraud_waiting || 0) + (hq.reports_waiting || 0);

  return (
    <div className="p-6 space-y-6 overflow-y-auto h-full" data-testid="workforce-overview">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Gem className="w-6 h-6 text-violet-500" /> AI Workforce — Diamond Overview
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Eleven agents on their own schedules · anything that needs a person lands in the escalation queue below</p>
          {failed.length > 0 && <p className="text-xs text-red-600 mt-1">Couldn't load: {failed.join(', ')} (retrying every minute)</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={toggleScanner}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${
              scannerActive ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid="scanner-toggle-btn">
            {scannerActive ? <><Power className="w-4 h-4" /> Loops ON</> : <><PowerOff className="w-4 h-4" /> Loops OFF</>}
          </button>
          <button onClick={runAll} disabled={running}
            className="flex items-center gap-1.5 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
            data-testid="run-all-btn">
            <Play className={`w-4 h-4 ${running ? 'animate-pulse' : ''}`} /> {running ? 'Starting...' : 'Run All Agents'}
          </button>
          <button onClick={() => fetchData(true)} className="flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        {[
          { label: 'Agents',          val: summary.total_agents || agents.length, color: 'text-violet-600' },
          { label: 'Active',          val: summary.active_agents || 0,  color: 'text-emerald-600' },
          { label: 'Auto-Running',    val: summary.active_autorun || 0, color: 'text-blue-600' },
          { label: 'In Error',        val: summary.error_agents || 0,   color: summary.error_agents ? 'text-red-600' : 'text-gray-500' },
          { label: 'Open Escalations',val: summary.open_escalations || 0, color: summary.open_escalations ? 'text-red-600' : 'text-gray-500' },
          { label: 'Waiting on a person', val: humanTotal, color: humanTotal ? 'text-amber-600' : 'text-gray-500' },
          { label: 'Unresolved Events', val: summary.pending_events || 0, color: 'text-amber-600' },
          { label: 'Runs (all agents)', val: trends?.total_scans || 0, color: 'text-cyan-600' },
        ].map(s => (
          <div key={s.label} className="bg-white border rounded-xl p-4 text-center">
            <p className={`text-2xl font-bold ${s.color}`}>{s.val}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Escalation queue */}
      <div className="bg-white border rounded-xl" data-testid="escalation-queue">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Bell className="w-4 h-4 text-red-500" /> Needs a person</h2>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <button onClick={() => navigate('/support-tickets')} className="hover:underline">Tickets {hq.tickets_waiting || 0}</button>
            <button onClick={() => navigate('/moderation')} className="hover:underline">Moderation {(hq.violations_waiting || 0) + (hq.reports_waiting || 0)}</button>
            <button onClick={() => navigate('/workforce/obsidian')} className="hover:underline">Wallet reviews {hq.fraud_waiting || 0}</button>
            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{escalations.length} open</span>
          </div>
        </div>
        <div className="divide-y max-h-80 overflow-y-auto">
          {escalations.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">Nothing waiting on you. The agents will ring the bell (and email for high/critical) when something needs a decision.</div>
          ) : escalations.map(e => <EscalationRow key={e.id} e={e} onResolve={resolveEscalation} />)}
        </div>
      </div>

      {/* Health Heatmap + Severity Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Agent Health Heatmap</h3>
          <HealthHeatmap agents={agents} />
          <div className="flex gap-3 mt-3 text-[10px] text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-emerald-500" />Active</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-blue-500" />Processing</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-gray-300" />Idle</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-red-500" />Error</span>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">24h Event Severity Breakdown</h3>
          <SeverityBar data={sevBreakdown} />
          <div className="grid grid-cols-4 gap-2 mt-3">
            {['critical', 'high', 'medium', 'low'].map(sev => (
              <div key={sev} className="text-center">
                <p className="text-lg font-bold" style={{ color: SEV_COLORS[sev] }}>{sevBreakdown[sev] || 0}</p>
                <p className="text-[10px] text-gray-500 capitalize">{sev}</p>
              </div>
            ))}
          </div>
          {trends?.events_by_agent && (
            <div className="mt-3 pt-3 border-t">
              <h4 className="text-[10px] font-bold text-gray-400 uppercase mb-1.5">Events by Agent (24h)</h4>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(trends.events_by_agent).filter(([, v]) => v > 0).map(([name, count]) => (
                  <span key={name} className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 font-medium">
                    {name} <span className="font-bold">{count}</span>
                  </span>
                ))}
                {Object.values(trends.events_by_agent).every(v => v === 0) && (
                  <span className="text-xs text-gray-400">No events in 24h</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <div>
          <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Lightbulb className="w-4 h-4 text-amber-500" /> What the workforce knows right now</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {insights.slice(0, 12).map(ins => <InsightCard key={`${ins.agent}-${ins.key}`} ins={ins} />)}
          </div>
        </div>
      )}

      {/* Agent Cards Grid */}
      <div>
        <h2 className="font-semibold text-gray-900 mb-3">All Agents</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {agents.map(a => {
            const meta = AGENTS.find(x => x.key === a.name) || { icon: Activity, color: '#6b7280' };
            const Icon = meta.icon;
            const hb = heartbeats[a.name] || {};
            return (
              <div key={a.name}
                className="bg-white border rounded-xl p-4 cursor-pointer hover:shadow-md transition group"
                onClick={() => navigate(`/workforce/${a.name.toLowerCase()}`)}
                data-testid={`agent-card-${a.name.toLowerCase()}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: meta.color }}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-sm text-gray-900 truncate">{a.name}</h3>
                  </div>
                  <HeartbeatDot alive={hb.alive} secondsSince={hb.seconds_since} />
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500" />
                </div>
                <p className="text-xs text-gray-500 mb-2 line-clamp-1">{a.purpose || a.description}</p>
                <div className="flex items-center justify-between">
                  <StatusBadge status={a.status} />
                  <span className="text-[10px] text-gray-400">{a.autorun ? `every ${fmtInterval(a.autorun_interval)} · ran ${ago(a.last_scan)}` : 'paused'}</span>
                </div>
                {a.last_error && a.status === 'error' && <p className="text-[10px] text-red-600 mt-1 truncate" title={a.last_error}>{a.last_error}</p>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Cross-Agent Events */}
      <div className="bg-white border rounded-xl">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Recent Cross-Agent Events</h2>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{events.length}</span>
        </div>
        <div className="divide-y max-h-72 overflow-y-auto">
          {events.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">No events yet. Agents only write an event when they find something.</div>
          ) : events.slice(0, 15).map(ev => (
            <div key={ev.event_id} className="px-5 py-3 flex items-start gap-3 text-sm">
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 mt-0.5 ${SEVERITY_STYLES[ev.severity] || ''}`}>{ev.severity}</span>
              <div className="min-w-0">
                <span className="font-medium text-gray-900">{ev.title}</span>
                <span className="text-gray-500 ml-2 text-xs">{ev.agent_name}</span>
                <p className="text-xs text-gray-500 truncate">{ev.description}</p>
              </div>
              <span className="text-[10px] text-gray-400 shrink-0 ml-auto">{ev.created_at ? new Date(ev.created_at).toLocaleTimeString() : ''}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Risk Summary */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-700 rounded-xl p-5 text-white">
        <h3 className="font-bold mb-1">System Risk Summary</h3>
        <p className="text-sm text-violet-200">
          {summary.critical_escalations > 0 ? `${summary.critical_escalations} critical escalation(s) need you now. ` : summary.critical_events > 0 ? `${summary.critical_events} critical event(s) unresolved. ` : 'No critical items. '}
          {summary.high_events > 0 ? `${summary.high_events} high-priority event(s) pending. ` : ''}
          {dashboard?.workforce_summary?.summary || ''}
          {scannerActive ? '' : ' Background loops are OFF — heartbeats and retention are paused.'}
        </p>
      </div>
    </div>
  );
};


/* ─── AGENT ACTION PANELS ─── */

const OnyxActionPanel = ({ api, fetchAll }) => {
  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('warn');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get('/api/admin/workforce/onyx/flagged-users').then(r => setHistory(r.data.actions || [])).catch(() => {});
  }, [api]);

  const submit = async () => {
    if (!userId) return toast.error('User ID is required');
    setLoading(true);
    try {
      await api.post('/api/admin/workforce/onyx/moderate', { user_id: userId, action, reason });
      toast.success(`${action} applied`);
      setUserId(''); setReason('');
      fetchAll();
      api.get('/api/admin/workforce/onyx/flagged-users').then(r => setHistory(r.data.actions || []));
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="onyx-action-panel">
      <h3 className="text-sm font-semibold text-gray-700">Manual moderation (same enforcement as the automated policy)</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <input placeholder="User ID" value={userId} onChange={e => setUserId(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm" data-testid="onyx-user-id" />
        <select value={action} onChange={e => setAction(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" data-testid="onyx-action-select">
          <option value="warn">Warn (notice)</option>
          <option value="mute">Mute chat (1h)</option>
          <option value="unmute">Unmute</option>
          <option value="suspend">Suspend (3 days)</option>
          <option value="ban">Ban (permanent)</option>
          <option value="unban">Lift suspension / ban</option>
        </select>
        <input placeholder="Reason (shown to the member)" value={reason} onChange={e => setReason(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm" data-testid="onyx-reason" />
        <button onClick={submit} disabled={loading}
          className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
          data-testid="onyx-submit-btn">
          {loading ? 'Applying...' : 'Apply'}
        </button>
      </div>
      {history.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-600">Recent Actions</div>
          <div className="divide-y max-h-48 overflow-y-auto">
            {history.slice(0, 10).map(a => (
              <div key={a.action_id} className="px-3 py-2 text-xs flex justify-between">
                <span><span className="font-medium">{a.action_type}</span> — {a.target_id?.slice(0, 12)}... — {a.reason}</span>
                <span className="text-gray-400">{a.executed_at ? new Date(a.executed_at).toLocaleString() : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ObsidianActionPanel = ({ api, fetchAll }) => {
  const [userId, setUserId] = useState('');
  const [txnId, setTxnId] = useState('');
  const [reason, setReason] = useState('');
  const [flagType, setFlagType] = useState('suspicious');
  const [loading, setLoading] = useState(false);
  const [flags, setFlags] = useState([]);

  useEffect(() => {
    api.get('/api/admin/workforce/obsidian/fraud-flags').then(r => setFlags(r.data.flags || [])).catch(() => {});
  }, [api]);

  const submit = async () => {
    if (!userId && !txnId) return toast.error('User ID or Transaction ID required');
    setLoading(true);
    try {
      await api.post('/api/admin/workforce/obsidian/flag-transaction', { user_id: userId, transaction_id: txnId, reason, flag_type: flagType });
      toast.success('Flag recorded');
      setUserId(''); setTxnId(''); setReason('');
      fetchAll();
      api.get('/api/admin/workforce/obsidian/fraud-flags').then(r => setFlags(r.data.flags || []));
    } catch (e) { toast.error(e.response?.data?.detail || 'Flag failed'); }
    finally { setLoading(false); }
  };

  const resolveFlag = async (flagId) => {
    try {
      await api.post(`/api/admin/workforce/obsidian/fraud-flags/${flagId}/resolve`);
      toast.success('Flag resolved');
      setFlags(prev => prev.map(f => f.id === flagId ? { ...f, status: 'resolved' } : f));
    } catch { toast.error('Resolve failed'); }
  };

  return (
    <div className="space-y-4" data-testid="obsidian-action-panel">
      <h3 className="text-sm font-semibold text-gray-700">Manual fraud flag (wallet actions live on the Obsidian console)</h3>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
        <input placeholder="User ID" value={userId} onChange={e => setUserId(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" data-testid="obsidian-user-id" />
        <input placeholder="Transaction ID" value={txnId} onChange={e => setTxnId(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" data-testid="obsidian-txn-id" />
        <select value={flagType} onChange={e => setFlagType(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" data-testid="obsidian-flag-type">
          <option value="suspicious">Suspicious</option>
          <option value="fraud">Fraud</option>
          <option value="chargeback_risk">Chargeback Risk</option>
        </select>
        <input placeholder="Reason" value={reason} onChange={e => setReason(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" data-testid="obsidian-reason" />
        <button onClick={submit} disabled={loading} className="bg-purple-900 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-purple-950 disabled:opacity-50" data-testid="obsidian-submit-btn">
          {loading ? 'Flagging...' : 'Flag'}
        </button>
      </div>
      {flags.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-600">Fraud Flags</div>
          <div className="divide-y max-h-48 overflow-y-auto">
            {flags.slice(0, 10).map(f => (
              <div key={f.id} className="px-3 py-2 text-xs flex items-center justify-between">
                <div>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${f.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{f.status}</span>
                  <span className="ml-2 font-medium">{f.flag_type}</span>
                  <span className="ml-2 text-gray-500">{f.reason}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">{f.created_at ? new Date(f.created_at).toLocaleString() : ''}</span>
                  {f.status !== 'resolved' && <button onClick={() => resolveFlag(f.id)} className="text-emerald-600 hover:text-emerald-700 font-medium">Resolve</button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const TopazActionPanel = ({ api, fetchAll }) => {
  const [ticketId, setTicketId] = useState('');
  const [resolution, setResolution] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!ticketId) return toast.error('Ticket ID is required');
    setLoading(true);
    try {
      await api.post('/api/admin/workforce/topaz/resolve-ticket', { ticket_id: ticketId, resolution });
      toast.success('Ticket resolved');
      setTicketId(''); setResolution('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Resolve failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="topaz-action-panel">
      <h3 className="text-sm font-semibold text-gray-700">Quick resolve (the full console is the Topaz tab)</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <input placeholder="Ticket ID" value={ticketId} onChange={e => setTicketId(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" data-testid="topaz-ticket-id" />
        <input placeholder="Closing message (sent to the member)" value={resolution} onChange={e => setResolution(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" data-testid="topaz-resolution" />
        <button onClick={submit} disabled={loading} className="bg-amber-500 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-amber-600 disabled:opacity-50" data-testid="topaz-submit-btn">
          {loading ? 'Resolving...' : 'Resolve Ticket'}
        </button>
      </div>
    </div>
  );
};

// Round 39: the same console serves Citrine's Emerald campaigns (`agent` picks whose proposals show).
const PeridotPanel = ({ api, agent = 'PERIDOT' }) => {
  const [overview, setOverview] = useState(null);
  const [audiences, setAudiences] = useState([]);
  const [busy, setBusy] = useState('');
  const [editing, setEditing] = useState({});
  const [custom, setCustom] = useState({ title: '', audience: 'active_7d', push_title: '', push_body: '', rationale: '' });

  const load = useCallback(async () => {
    try {
      const [o, a] = await Promise.all([api.get('/api/admin/workforce/peridot/overview', { params: { agent } }), api.get('/api/admin/workforce/peridot/audiences')]);
      setOverview(o.data); setAudiences(a.data.audiences || []);
    } catch { toast.error(`Failed to load ${agent === 'CITRINE' ? 'Citrine' : 'Peridot'}`); }
  }, [api, agent]);
  useEffect(() => { load(); }, [load]);

  const send = async (c) => {
    const edit = editing[c.id] || {};
    if (!window.confirm(`Send "${edit.push_title ?? c.push_title}" to ${c.audience_label} (${c.audience_size} members)? This pushes a notification to real members.`)) return;
    setBusy(c.id);
    try {
      const r = await api.post(`/api/admin/workforce/peridot/campaigns/${c.id}/send`, { push_title: edit.push_title, push_body: edit.push_body });
      toast.success(`Sent to ${r.data.sent_count} members (${r.data.pushed} push tokens)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setBusy(''); }
  };
  const dismiss = async (id) => {
    try { await api.post(`/api/admin/workforce/peridot/campaigns/${id}/dismiss`); load(); } catch { toast.error('Dismiss failed'); }
  };
  const createCustom = async () => {
    if (!custom.title || !custom.push_title || !custom.push_body) return toast.error('Title, push title and push body are required');
    setBusy('custom');
    try { await api.post('/api/admin/workforce/peridot/campaigns', custom); toast.success('Campaign proposed — press Send when ready'); setCustom({ title: '', audience: 'active_7d', push_title: '', push_body: '', rationale: '' }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
    finally { setBusy(''); }
  };

  const growth = overview?.growth?.data || {};
  const campaigns = overview?.campaigns || [];
  return (
    <div className="space-y-5" data-testid="peridot-panel">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {[
          { label: 'Members', val: growth.members },
          { label: 'Signups 7d', val: growth.signups_week },
          { label: 'Referral signups 7d', val: growth.referred_week },
          { label: 'DAU / WAU', val: `${growth.dau ?? 0} / ${growth.wau ?? 0}` },
          { label: 'Push enabled', val: growth.push_users },
          { label: 'Waitlist', val: growth.waitlist },
        ].map(m => (
          <div key={m.label} className="bg-white border rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-gray-800">{m.val ?? 0}</p>
            <p className="text-[10px] text-gray-500">{m.label}</p>
          </div>
        ))}
      </div>
      {growth.signups_series && (
        <div className="bg-white border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Signups — last 14 days</h3>
          <div className="flex items-end gap-1 h-20">
            {growth.signups_series.map(d => {
              const max = Math.max(1, ...growth.signups_series.map(x => x.count));
              return <div key={d.day} title={`${d.day}: ${d.count}`} className="flex-1 bg-lime-400 rounded-t" style={{ height: `${Math.max(4, (d.count / max) * 100)}%` }} />;
            })}
          </div>
        </div>
      )}
      <div className="bg-white border rounded-xl">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Megaphone className="w-4 h-4 text-lime-600" /> Campaign proposals</h3>
          <span className="text-[10px] text-gray-500">Nothing is sent until you press Send. Push + in-app notification.</span>
        </div>
        <div className="divide-y">
          {campaigns.length === 0 && <div className="p-6 text-center text-gray-400 text-sm">{agent === 'CITRINE' ? 'No proposals yet — Citrine proposes an Emerald nudge when enough members are running low, have lapsed, or have never bought.' : 'No proposals yet — Peridot proposes when an audience is big enough.'}</div>}
          {campaigns.map(c => {
            const e = editing[c.id] || {};
            const proposed = c.status === 'proposed';
            return (
              <div key={c.id} className="p-4 space-y-2" data-testid={`campaign-${c.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <span className="font-semibold text-sm text-gray-900">{c.title}</span>
                    <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full font-semibold ${c.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : c.status === 'dismissed' ? 'bg-gray-100 text-gray-500' : 'bg-lime-100 text-lime-800'}`}>{c.status}</span>
                  </div>
                  <span className="text-[10px] text-gray-500 flex items-center gap-1"><Users className="w-3 h-3" /> {c.audience_label} · {c.audience_size} members{c.status === 'sent' ? ` · sent ${c.sent_count} (${c.pushed || 0} pushes) ${ago(c.sent_at)}` : ''}</span>
                </div>
                <p className="text-xs text-gray-500">{c.rationale}</p>
                {proposed ? (
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto_auto] gap-2 items-center">
                    <input value={e.push_title ?? c.push_title} onChange={ev => setEditing(p => ({ ...p, [c.id]: { ...e, push_title: ev.target.value } }))} className="border rounded-lg px-3 py-1.5 text-sm" maxLength={80} />
                    <input value={e.push_body ?? c.push_body} onChange={ev => setEditing(p => ({ ...p, [c.id]: { ...e, push_body: ev.target.value } }))} className="border rounded-lg px-3 py-1.5 text-sm" maxLength={180} />
                    <button onClick={() => send(c)} disabled={busy === c.id} className="flex items-center gap-1 px-3 py-1.5 bg-lime-600 text-white rounded-lg text-sm font-medium hover:bg-lime-700 disabled:opacity-50" data-testid={`send-campaign-${c.id}`}><Send className="w-3.5 h-3.5" /> {busy === c.id ? 'Sending...' : 'Send'}</button>
                    <button onClick={() => dismiss(c.id)} className="px-2 py-1.5 border rounded-lg text-sm text-gray-500 hover:bg-gray-50" title="Dismiss"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-800"><span className="font-medium">{c.push_title}</span> — {c.push_body}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="bg-white border rounded-xl p-4 space-y-2">
        <h3 className="text-sm font-semibold text-gray-700">Propose your own campaign</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input placeholder="Internal title" value={custom.title} onChange={e => setCustom(c => ({ ...c, title: e.target.value }))} className="border rounded-lg px-3 py-1.5 text-sm" data-testid="custom-title" />
          <select value={custom.audience} onChange={e => setCustom(c => ({ ...c, audience: e.target.value }))} className="border rounded-lg px-3 py-1.5 text-sm" data-testid="custom-audience">
            {audiences.map(a => <option key={a.key} value={a.key}>{a.label} ({a.size})</option>)}
          </select>
          <input placeholder="Push title (≤80)" maxLength={80} value={custom.push_title} onChange={e => setCustom(c => ({ ...c, push_title: e.target.value }))} className="border rounded-lg px-3 py-1.5 text-sm" data-testid="custom-push-title" />
          <input placeholder="Push body (≤180)" maxLength={180} value={custom.push_body} onChange={e => setCustom(c => ({ ...c, push_body: e.target.value }))} className="border rounded-lg px-3 py-1.5 text-sm" data-testid="custom-push-body" />
        </div>
        <button onClick={createCustom} disabled={busy === 'custom'} className="px-3 py-1.5 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 disabled:opacity-50" data-testid="custom-create-btn">Add proposal</button>
      </div>
    </div>
  );
};


/* ─── INDIVIDUAL AGENT DASHBOARD ─── */
const AgentDashboard = () => {
  const { agentName } = useParams();
  const { api } = useAdmin();
  const navigate = useNavigate();
  const name = agentName?.toUpperCase();
  const meta = AGENTS.find(a => a.key === name) || { icon: Activity, color: '#6b7280', label: name };
  const Icon = meta.icon;

  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [events, setEvents] = useState([]);
  const [insights, setInsights] = useState([]);
  const [escalations, setEscalations] = useState([]);   // Round 26
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [autoInterval, setAutoInterval] = useState(0);
  const [tab, setTab] = useState('overview');
  const inFlight = useRef(false);

  const fetchAll = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const [sRes, lRes, mRes, eRes, iRes, xRes] = await Promise.all([
        api.get(`/api/admin/workforce/agents/${name}/status`),
        api.get(`/api/admin/workforce/agents/${name}/logs?limit=50`),
        api.get(`/api/admin/workforce/agents/${name}/metrics`),
        api.get(`/api/admin/workforce/agents/${name}/events?limit=30`),
        api.get(`/api/admin/workforce/agents/${name}/insights`),
        api.get(`/api/admin/workforce/escalations?agent=${name}&status=all&limit=30`).catch(() => ({ data: { escalations: [] } })),
      ]);
      setStatus(sRes.data);
      setLogs(lRes.data.logs || []);
      setMetrics(mRes.data);
      setEvents(eRes.data.events || []);
      setInsights(iRes.data.insights || []);
      setEscalations(xRes.data.escalations || []);
      setAutoInterval(prev => prev || sRes.data.autorun_interval || 300);
    } catch { toast.error('Failed to load agent data'); }
    finally { inFlight.current = false; setLoading(false); }
  }, [api, name]);

  useEffect(() => { setLoading(true); setTab('overview'); fetchAll(); const i = setInterval(fetchAll, 30000); return () => clearInterval(i); }, [fetchAll]);

  const doAction = async (action, label) => {
    setActionLoading(action);
    try {
      if (action === 'run') {
        const r = await api.post(`/api/admin/workforce/agents/${name}/run`);
        if (r.data.skipped) toast.info(`${meta.label} is already running`);
        else if (r.data.timed_out) toast.error(`${meta.label} timed out after ${status?.timeout_seconds || 120}s`);
        else toast.success(`${meta.label}: ${r.data.events_generated} event(s) in ${r.data.duration_ms} ms`);
      }
      else if (action === 'autorun_start') { await api.post(`/api/admin/workforce/agents/${name}/autorun/start?interval=${autoInterval}`); toast.success(`${meta.label}: ${label}`); }
      else if (action === 'autorun_stop') { await api.post(`/api/admin/workforce/agents/${name}/autorun/stop`); toast.success(`${meta.label}: ${label}`); }
      else if (action === 'restart') { await api.post(`/api/admin/workforce/agents/${name}/restart`); toast.success(`${meta.label}: ${label}`); }
      fetchAll();
    } catch { toast.error(`Failed: ${label}`); }
    finally { setActionLoading(''); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 animate-spin" style={{ color: meta.color }} /></div>;
  const isAutorunning = status?.autorun;
  const hasActions = ['ONYX', 'OBSIDIAN', 'TOPAZ', 'PERIDOT', 'CITRINE'].includes(name);   // Round 39: Citrine proposes Emerald campaigns
  const tabs = ['overview', 'insights', 'events', 'escalations', 'logs'];
  if (hasActions) tabs.push(name === 'PERIDOT' || name === 'CITRINE' ? 'campaigns' : 'actions');
  const minInt = status?.min_interval || 60;
  const intervalOptions = [30, 60, 120, 300, 600, 1800, 3600].filter(v => v >= minInt);

  return (
    <div className="p-6 space-y-5 overflow-y-auto h-full" data-testid={`agent-dashboard-${name?.toLowerCase()}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: meta.color }}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{meta.label}</h1>
            <p className="text-sm text-gray-500">{status?.purpose}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <HeartbeatDot alive={status?.alive} secondsSince={null} />
          <StatusBadge status={status?.status} />
          {isAutorunning && <span className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded-full font-medium flex items-center gap-1"><Radio className="w-3 h-3" />every {fmtInterval(status?.autorun_interval)}</span>}
        </div>
      </div>

      {status?.last_error && status?.status === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2 text-xs text-red-700" data-testid="agent-last-error">Last error: {status.last_error}</div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 bg-white border rounded-xl p-4">
        <button onClick={() => doAction('run', 'Run once')} disabled={!!actionLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
          data-testid={`agent-run-${name?.toLowerCase()}`}>
          <Play className="w-3.5 h-3.5" /> {actionLoading === 'run' ? 'Running...' : 'Run Once'}
        </button>
        {isAutorunning ? (
          <button onClick={() => doAction('autorun_stop', 'Auto-run stopped')} disabled={!!actionLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50"
            data-testid={`agent-autorun-stop-${name?.toLowerCase()}`}>
            <Pause className="w-3.5 h-3.5" /> Stop Auto-Run
          </button>
        ) : (
          <div className="flex items-center gap-1.5">
            <button onClick={() => doAction('autorun_start', `Auto-run started (${fmtInterval(autoInterval)})`)} disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              data-testid={`agent-autorun-start-${name?.toLowerCase()}`}>
              <Radio className="w-3.5 h-3.5" /> Start Auto-Run
            </button>
            <select value={autoInterval} onChange={e => setAutoInterval(Number(e.target.value))}
              className="text-xs border rounded-lg px-2 py-1.5 bg-white"
              data-testid={`agent-interval-${name?.toLowerCase()}`}>
              {intervalOptions.map(v => <option key={v} value={v}>{fmtInterval(v)}</option>)}
            </select>
          </div>
        )}
        <button onClick={() => doAction('restart', 'Restarted')} disabled={!!actionLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          data-testid={`agent-restart-${name?.toLowerCase()}`}>
          <RotateCw className="w-3.5 h-3.5" /> Restart
        </button>
        <span className="text-[11px] text-gray-400 ml-2">min interval {fmtInterval(minInt)} · timeout {status?.timeout_seconds || 120}s</span>
        <button onClick={fetchAll} className="flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm text-gray-600 hover:bg-gray-50 ml-auto">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <InfoCard label="Events (7d)" value={metrics?.events_total || 0} icon={<Zap className="w-4 h-4 text-amber-500" />} />
        <InfoCard label="Actions (7d)" value={metrics?.actions_total || 0} icon={<Activity className="w-4 h-4 text-blue-500" />} />
        <InfoCard label="Escalations (7d)" value={metrics?.escalations_total || 0} icon={<Bell className="w-4 h-4 text-red-500" />} />
        <InfoCard label="Errors (7d)" value={metrics?.errors_total || 0} icon={<AlertCircle className="w-4 h-4 text-red-500" />} />
        <InfoCard label="Runs" value={metrics?.scan_count || 0} icon={<BarChart3 className="w-4 h-4 text-violet-500" />} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === t ? 'border-violet-600 text-violet-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid={`tab-${t}`}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'overview' && (
        <div className="bg-white border rounded-xl p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Purpose & Responsibilities</h3>
              <p className="text-sm text-gray-600">{status?.purpose}</p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Monitors</h3>
              <p className="text-sm text-gray-600">{status?.monitors}</p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Category</h3>
              <p className="text-sm text-gray-600">{status?.category}</p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Auto-Run</h3>
              <p className="text-sm text-gray-600">
                {isAutorunning ? `Active — every ${fmtInterval(status?.autorun_interval)}` : 'Inactive'}
              </p>
              {status?.last_scan && <p className="text-xs text-gray-400 mt-0.5">Last run {ago(status.last_scan)}: {status.last_events} event(s) in {status.last_duration_ms} ms</p>}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Heartbeat</h3>
              <p className="text-sm text-gray-600 flex items-center gap-2">
                <HeartbeatDot alive={status?.alive} secondsSince={null} />
                {status?.last_heartbeat ? `Last: ${new Date(status.last_heartbeat).toLocaleTimeString()}` : 'No heartbeat yet'}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Uptime</h3>
              <p className="text-sm text-gray-600">{metrics?.uptime_seconds ? `${Math.round(metrics.uptime_seconds / 60)} minutes` : 'N/A'}</p>
            </div>
          </div>
          {insights.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t">
              {insights.slice(0, 2).map(ins => <InsightCard key={ins.key} ins={ins} />)}
            </div>
          )}
        </div>
      )}

      {tab === 'insights' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="agent-insights">
          {insights.length === 0 ? <div className="p-6 text-center text-gray-400 text-sm col-span-2">No insights yet — run the agent once.</div>
            : insights.map(ins => <InsightCard key={ins.key} ins={ins} />)}
        </div>
      )}

      {tab === 'events' && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="divide-y max-h-96 overflow-y-auto">
            {events.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">No events. Agents only write an event when they find something.</div>
            ) : events.map(ev => (
              <div key={ev.event_id} className="px-5 py-3 flex items-start gap-3 text-sm">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 mt-0.5 ${SEVERITY_STYLES[ev.severity] || ''}`}>{ev.severity}</span>
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-gray-900">{ev.title}</span>
                  {ev.resolved && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">RESOLVED</span>}
                  <p className="text-xs text-gray-500 mt-0.5">{ev.description}</p>
                </div>
                <span className="text-[10px] text-gray-400 shrink-0">{ev.created_at ? new Date(ev.created_at).toLocaleString() : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'escalations' && (
        <div className="bg-white border rounded-xl overflow-hidden" data-testid="agent-escalations">
          <div className="divide-y max-h-96 overflow-y-auto">
            {escalations.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">This agent hasn't handed anything to a person.</div>
            ) : escalations.map(e => (
              <button key={e.id} onClick={() => navigate(`/workforce/${name.toLowerCase()}?escalation=${e.id}`)} className="w-full text-left px-5 py-3 flex items-start gap-3 text-sm hover:bg-gray-50" data-testid={`agent-escalation-${e.id}`}>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase shrink-0 mt-0.5 ${SEVERITY_STYLES[e.severity] || ''}`}>{e.severity}</span>
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-gray-900">{e.title}</span>
                  <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${e.status === 'open' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>{e.status === 'open' ? 'NEEDS ATTENTION' : 'RESOLVED'}</span>
                  <p className="text-xs text-gray-500 line-clamp-2">{e.message}</p>
                </div>
                <span className="text-[10px] text-gray-400 shrink-0">{ago(e.created_at)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {tab === 'logs' && (
        <div className="bg-gray-900 rounded-xl p-4 font-mono text-xs max-h-96 overflow-y-auto" data-testid="agent-logs-panel">
          {logs.length === 0 ? (
            <p className="text-gray-500">No logs yet. Logs are written when a run finds something or fails.</p>
          ) : logs.map((l, i) => (
            <div key={i} className="py-0.5">
              <span className="text-gray-500">{l.timestamp ? new Date(l.timestamp).toLocaleTimeString() : ''}</span>{' '}
              <span className={l.level === 'error' ? 'text-red-400' : l.level === 'warning' ? 'text-amber-400' : 'text-emerald-400'}>
                [{l.level?.toUpperCase()}]
              </span>{' '}
              <span className="text-gray-200">{l.message}</span>
            </div>
          ))}
        </div>
      )}

      {tab === 'actions' && (
        <div className="bg-white border rounded-xl p-5">
          {name === 'ONYX' && <OnyxActionPanel api={api} fetchAll={fetchAll} />}
          {name === 'OBSIDIAN' && <ObsidianActionPanel api={api} fetchAll={fetchAll} />}
          {name === 'TOPAZ' && <TopazActionPanel api={api} fetchAll={fetchAll} />}
        </div>
      )}

      {tab === 'campaigns' && (name === 'PERIDOT' || name === 'CITRINE') && <PeridotPanel api={api} agent={name} />}
    </div>
  );
};


/* ─── INFO CARD ─── */
const InfoCard = ({ label, value, icon }) => (
  <div className="bg-white border rounded-xl p-4 flex items-center gap-3">
    {icon}
    <div>
      <p className="text-lg font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  </div>
);


/* ─── MAIN LAYOUT ─── */
const WorkforcePage = () => {
  const { agentName } = useParams();
  const { api, admin } = useAdmin();
  const location = useLocation();
  const navigate = useNavigate();
  const activeAgent = agentName?.toUpperCase() || null;
  const [escalationCount, setEscalationCount] = useState(0);
  // Round 26: ?escalation=<id> (bell / push / email deep link) opens the panel
  const escalationId = new URLSearchParams(location.search).get('escalation');

  useEffect(() => {
    let cancelled = false;
    const load = () => api.get('/api/admin/workforce/escalations/count').then(r => { if (!cancelled) setEscalationCount(r.data.open || 0); }).catch(() => {});
    load();
    const i = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(i); };
  }, [api]);

  const renderContent = () => {
    if (!activeAgent) return <OverviewPage onEscalationCount={setEscalationCount} />;
    // Round 39: an agent this admin was not given
    if (!canAccessAgent(admin, activeAgent)) return (
      <div className="p-6 text-sm text-gray-500 flex items-center gap-2" data-testid="workforce-agent-locked">
        <Lock className="w-4 h-4" /> You don't have access to this agent. A super admin can open it for you from Admin Management.
      </div>
    );
    if (activeAgent === 'OPAL') return <div className="overflow-y-auto h-full"><OpalPage /></div>;
    if (activeAgent === 'TOPAZ') return <TopazDashboard />;
    if (activeAgent === 'ONYX') return <OnyxDashboard />;
    if (activeAgent === 'OBSIDIAN') return <ObsidianDashboard />;
    if (activeAgent === 'LAPIS') return <div className="overflow-y-auto h-full"><LapisPage /></div>;
    if (activeAgent === 'TANZANITE') return <div className="overflow-y-auto h-full"><TanzanitePage /></div>;
    if (activeAgent === 'TOURMALINE') return <div className="overflow-y-auto h-full"><TourmalinePage /></div>;
    if (activeAgent === 'SPINEL') return <div className="overflow-y-auto h-full"><SpinelPage /></div>;
    if (activeAgent === 'AMETHYST') return <div className="overflow-y-auto h-full"><AmethystPage /></div>;
    return <AgentDashboard />;
  };

  return (
    <div className="flex h-[calc(100vh-64px)]" data-testid="workforce-page">
      <AgentNav activeAgent={activeAgent} escalationCount={escalationCount} />
      <div className="flex-1 overflow-hidden flex flex-col">
        {escalationId && (
          <div className="p-4 pb-0 overflow-y-auto max-h-[60%]">
            <EscalationPanel escalationId={escalationId} onClose={() => navigate(location.pathname)} />
          </div>
        )}
        <div className="flex-1 overflow-hidden">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default WorkforcePage;
